/**
 * Voice-note audio waiting to upload (services/notes/voice-note-audio on the
 * server). The recording is recorded here first, then its note is created,
 * then the file uploads through the ordinary document path as
 * `voice-note-<note id>.<ext>`, and the server transcribes it into the note.
 *
 * The file stays on the phone until its upload succeeds:
 *   - it is moved out of the cache (which the OS may clear) into the app's
 *     documents folder, and the record keeps its path relative to that folder
 *     (the absolute path changes across app updates on iOS);
 *   - the record is written BEFORE the note is created, carrying a client ref
 *     the note is created with (metadata.client_ref). An app killed in
 *     between, or a create whose answer was lost, is finished on the next run
 *     by finding the note by that ref first, so a note is never made twice and
 *     never left without its recording;
 *   - a small record per account and member (AsyncStorage, the visit
 *     recorder's manifest pattern) lists what is still to send; every change
 *     to it goes through one lock, so the drain, a resume and a new note never
 *     overwrite each other's writes;
 *   - a failed upload is retried with backoff (at most MAX_ATTEMPTS times),
 *     again when the app comes to the foreground, and on the next launch;
 *   - a refusal that can't succeed on retry (a 4xx: the note is gone, the
 *     upload is invalid, the storage is full) or a missing local file stops
 *     the retries: the card shows "Couldn't upload" with Retry and Discard;
 *   - only once the server has it is the local file deleted.
 * Uploads are fenced to the member who recorded them: a sign-out or account
 * switch mid-upload never writes into, or sends from, the other one's list.
 * A browser's recording is an in-memory blob: it is sent at once and can't
 * survive a reload.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AppState, Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import debug from '../utilities/debug';

const KEY = '@eleplan/voice_note_uploads';
const BASE_DELAY_MS = 5000;
const MAX_DELAY_MS = 10 * 60 * 1000;
/** About 6 hours of backoff in all; after that the card asks. */
export const MAX_ATTEMPTS = 12;
const UPLOAD_DIR = 'voice-note-uploads/';

export interface PendingVoiceNoteUpload {
  /** Client ref: the note is created with metadata.client_ref = ref. */
  ref: string;
  noteId: number | null;
  projectId: number;
  title: string;
  /** Relative to documentDirectory; an absolute uri only when it couldn't be moved (web blobs). */
  path: string;
  mime: string;
  attempts: number;
  nextAt: number;
  /** Stopped retrying: waits for Retry or Discard. */
  failed?: boolean;
}

export type VoiceNoteUploadState = 'uploading' | 'retrying' | 'failed' | 'done';

const persists = Platform.OS !== 'web';
const memory = new Map<string, PendingVoiceNoteUpload[]>();
/** Keyed `<member key>|note:<id>` and `<member key>|ref:<ref>`. */
const states = new Map<string, VoiceNoteUploadState>();
const listeners = new Set<() => void>();
let draining: Promise<void> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let appStateHooked = false;
let lock: Promise<unknown> = Promise.resolve();
/** Refs recorded this session whose note hasn't been asked for yet: no lookup needed before the first create. */
const fresh = new Set<string>();
/** One note creation per ref at a time (the save and a drain may both ask). */
const ensuring = new Map<string, Promise<number | null>>();

/** One read-modify-write of the pending list at a time. */
function locked<T>(fn: () => Promise<T>): Promise<T> {
  const run = lock.then(fn, fn);
  lock = run.catch(() => undefined);
  return run;
}

const changed = () => listeners.forEach((fn) => { try { fn(); } catch { /* a listener's own problem */ } });

export function subscribeVoiceNoteUploads(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// Loaded when first used: this module is imported by the recorder, which loads early.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const appStore = (): any => require('../store').store;

/** The signed-in member's list key, or null when no one is. */
function memberKey(): string | null {
  const s: any = appStore().getState();
  const accountId = s?.session?.account?.account_id;
  const cognitoId = s?.authentication?.user?.cognito_id;
  return accountId && cognitoId ? `${KEY}:${encodeURIComponent(accountId)}:${encodeURIComponent(cognitoId)}` : null;
}

const stateKey = (member: string, item: { noteId?: number | null; ref?: string }) =>
  (item.noteId != null ? `${member}|note:${item.noteId}` : `${member}|ref:${item.ref}`);

function setState(member: string, item: { noteId: number | null; ref: string }, state: VoiceNoteUploadState | null) {
  for (const key of [stateKey(member, { ref: item.ref }), ...(item.noteId != null ? [stateKey(member, { noteId: item.noteId })] : [])]) {
    if (state) states.set(key, state); else states.delete(key);
  }
}

/** Where this note's audio upload stands on this device for the signed-in member; undefined when there is none. */
export function voiceNoteUploadState(noteId: number | string | null | undefined, ref?: string | null): VoiceNoteUploadState | undefined {
  const member = memberKey();
  if (!member) return undefined;
  return (noteId != null ? states.get(stateKey(member, { noteId: Number(noteId) })) : undefined)
    ?? (ref ? states.get(stateKey(member, { ref })) : undefined);
}

const valid = (item: any): item is PendingVoiceNoteUpload => !!item && typeof item.ref === 'string'
  && typeof item.path === 'string' && (item.noteId === null || Number.isInteger(item.noteId));

async function load(member: string): Promise<PendingVoiceNoteUpload[]> {
  if (!persists) return [...(memory.get(member) || [])];
  try {
    const raw = await AsyncStorage.getItem(member);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(valid) : [];
  } catch {
    return [];
  }
}

async function save(member: string, list: PendingVoiceNoteUpload[]): Promise<void> {
  if (!persists) { memory.set(member, list); return; }
  try {
    if (list.length) await AsyncStorage.setItem(member, JSON.stringify(list));
    else await AsyncStorage.removeItem(member);
  } catch { /* retried from memory this session; the file stays */ }
}

/** Change one record under the lock; returns it as written (null when gone). */
function update(member: string, ref: string, fn: (item: PendingVoiceNoteUpload) => PendingVoiceNoteUpload | null): Promise<PendingVoiceNoteUpload | null> {
  return locked(async () => {
    const list = await load(member);
    const index = list.findIndex((p) => p.ref === ref);
    if (index < 0) return null;
    const next = fn(list[index]);
    if (next) list[index] = next; else list.splice(index, 1);
    await save(member, list);
    return next;
  });
}

const extensionOf = (mime: string, uri: string) => (/webm/.test(mime) || /\.webm$/i.test(uri) ? 'webm' : 'm4a');
const mimeOf = (ext: string) => (ext === 'webm' ? 'audio/webm' : 'audio/mp4');
export const voiceNoteAudioFilename = (noteId: number, ext: string) => `voice-note-${noteId}.${ext}`;
const isAbsolute = (path: string) => /^[a-z]+:/i.test(path);
/** The file's uri now, from its stored path. */
export const uriOf = (path: string) => (isAbsolute(path) ? path : `${FileSystem.documentDirectory || ''}${path}`);
const newRef = () => `vn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Keep the recording somewhere the OS won't clear until it has uploaded. Returns its stored path. */
async function keepFile(ref: string, uri: string, mime: string): Promise<string> {
  if (!persists || !FileSystem.documentDirectory || !uri.startsWith('file:')) return uri;
  const path = `${UPLOAD_DIR}voice-note-${ref}.${extensionOf(mime, uri)}`;
  try {
    await FileSystem.makeDirectoryAsync(`${FileSystem.documentDirectory}${UPLOAD_DIR}`, { intermediates: true });
    await FileSystem.moveAsync({ from: uri, to: uriOf(path) });
    return path;
  } catch {
    return uri;
  }
}

async function deleteLocal(path: string) {
  if (persists) await FileSystem.deleteAsync(uriOf(path), { idempotent: true }).catch(() => {});
}

/**
 * A voice note's recording, before its note exists: kept and recorded, so
 * whatever happens next it is uploaded, or the note made, on a later run.
 * Returns its ref and where the file now is; null when no one is signed in.
 */
export async function beginVoiceNoteUpload(item: { projectId: number; uri: string; mime: string; title: string }): Promise<{ ref: string; uri: string } | null> {
  const member = memberKey();
  if (!member) return null;
  const ref = newRef();
  const path = await keepFile(ref, item.uri, item.mime);
  await locked(async () => {
    const list = await load(member);
    list.push({ ref, noteId: null, projectId: item.projectId, title: item.title, path, mime: item.mime, attempts: 0, nextAt: 0 });
    await save(member, list);
  });
  fresh.add(ref);
  setState(member, { ref, noteId: null }, 'uploading');
  changed();
  return { ref, uri: uriOf(path) };
}

const noteIdFrom = (payload: any): number | null => {
  const id = Number(payload?.id ?? payload?.note_id ?? payload?.note?.id ?? (Array.isArray(payload) ? payload[0]?.id : undefined));
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * The note for a pending recording: the one already made with its ref, or a
 * new one. Its id is written to the record. Null when the server can't be
 * reached (the record stays, and this runs again on the next drain).
 */
export function ensureVoiceNote(ref: string): Promise<number | null> {
  const running = ensuring.get(ref);
  if (running) return running;
  const run = ensureVoiceNoteOnce(ref).finally(() => { ensuring.delete(ref); });
  ensuring.set(ref, run);
  return run;
}

async function ensureVoiceNoteOnce(ref: string): Promise<number | null> {
  const member = memberKey();
  if (!member) return null;
  const item = (await load(member)).find((p) => p.ref === ref);
  if (!item) return null;
  if (item.noteId != null) return item.noteId;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { createNote, fetchNotes } = require('../store/actions/notes');
  const store = appStore();
  // The ref, by this member: a plan's notes include other members' notes.
  const cognitoId = store.getState()?.authentication?.user?.cognito_id;
  const findByRef = () => ((store.getState()?.notes?.list?.[String(item.projectId)] || []) as any[])
    .find((n) => n?.metadata?.client_ref === ref && !!cognitoId && n?.cognito_id === cognitoId);
  // A note made by a create whose answer was lost, or before the app was killed.
  let id: number | null = null;
  const firstAsk = fresh.delete(ref);
  if (!firstAsk) {
    await Promise.resolve(store.dispatch(fetchNotes(item.projectId))).catch(() => {});
    id = noteIdFrom(findByRef());
  }
  if (id == null) {
    const res: any = await Promise.resolve(store.dispatch(createNote(item.projectId, {
      type: 'voice', query: item.title, metadata: { client_ref: ref },
    }))).catch(() => null);
    id = res?.success === false ? null : noteIdFrom(res?.payload);
    if (id == null) {
      // The answer may have been lost after the note was made: look before trying again.
      await Promise.resolve(store.dispatch(fetchNotes(item.projectId))).catch(() => {});
      id = noteIdFrom(findByRef());
    }
  }
  if (memberKey() !== member) return null; // signed out or switched meanwhile
  await update(member, ref, (p) => (id != null
    ? { ...p, noteId: id }
    : { ...p, attempts: p.attempts + 1, nextAt: Date.now() + backoff(p.attempts + 1) }));
  if (id != null) {
    setState(member, { ref, noteId: id }, 'uploading');
    changed();
  }
  return id;
}

const backoff = (attempts: number) => Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** Math.max(0, attempts - 1));

type UploadOutcome = 'ok' | 'retry' | 'permanent' | 'gone';

/**
 * A 4xx can't succeed on retry (the note is gone, the upload is invalid, the
 * storage is full), except an expired sign-in (401), a timeout (408) or a rate
 * limit (429). 410 means the note was deleted.
 */
function classify(res: any): UploadOutcome {
  if (res?.success) return 'ok';
  const status = Number(res?.status);
  if (status === 410) return 'gone';
  if (status >= 400 && status < 500 && ![401, 408, 429].includes(status)) return 'permanent';
  return 'retry';
}

async function uploadOne(item: PendingVoiceNoteUpload & { noteId: number }): Promise<UploadOutcome> {
  const uri = uriOf(item.path);
  if (persists) {
    const info: any = await FileSystem.getInfoAsync(uri).catch(() => null);
    if (info && info.exists === false) return 'permanent';
  }
  const ext = extensionOf(item.mime, item.path);
  const filename = voiceNoteAudioFilename(item.noteId, ext);
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { uploadDocument } = require('../store/actions/notes');
    const res: any = await (appStore().dispatch(uploadDocument(item.projectId, {
      filename,
      name: filename,
      document_type: 'voice_note_audio',
      mime: mimeOf(ext),
      type: ext,
    }, uri, mimeOf(ext), { onConflict: () => true }) as any) as unknown as Promise<any>);
    return classify(res);
  } catch {
    return 'retry';
  }
}

function schedule(list: PendingVoiceNoteUpload[]) {
  if (timer) { clearTimeout(timer); timer = null; }
  const due = list.filter((p) => !p.failed);
  if (!due.length) return;
  const wait = Math.max(1000, Math.min(...due.map((p) => p.nextAt)) - Date.now());
  timer = setTimeout(() => { timer = null; void drainVoiceNoteUploads(); }, wait);
}

/** Send what is due, one at a time; reschedule the rest with backoff. */
export function drainVoiceNoteUploads(): Promise<void> {
  if (draining) return draining;
  draining = (async () => {
    hookAppState();
    const member = memberKey();
    if (!member) return;
    for (const item of await load(member)) {
      if (memberKey() !== member) return; // fenced: never send for, or write to, another member
      if (item.failed || item.nextAt > Date.now()) continue;
      let noteId = item.noteId;
      if (noteId == null) noteId = await ensureVoiceNote(item.ref);
      if (noteId == null || memberKey() !== member) continue;
      setState(member, { ref: item.ref, noteId }, item.attempts ? 'retrying' : 'uploading');
      changed();
      const outcome = await uploadOne({ ...item, noteId });
      if (memberKey() !== member) return;
      const written = await update(member, item.ref, (current) => {
        if (outcome === 'ok' || outcome === 'gone') return null;
        if (outcome === 'permanent') return { ...current, noteId, failed: true };
        const attempts = current.attempts + 1;
        return attempts >= MAX_ATTEMPTS
          ? { ...current, noteId, attempts, failed: true }
          : { ...current, noteId, attempts, nextAt: Date.now() + backoff(attempts) };
      });
      if (outcome === 'ok' || outcome === 'gone') {
        // In, or its note was deleted: the phone's copy goes either way.
        await deleteLocal(item.path);
        setState(member, { ref: item.ref, noteId }, outcome === 'ok' ? 'done' : null);
        debug.log('voice', '[voiceNoteUploads] finished', { note_id: noteId, outcome, attempts: item.attempts + 1 });
      } else if (written?.failed) {
        setState(member, { ref: item.ref, noteId }, 'failed');
        debug.warn('[voiceNoteUploads] upload stopped; waiting for Retry or Discard', { note_id: noteId, outcome, attempts: written.attempts });
      } else if (written) {
        setState(member, { ref: item.ref, noteId }, 'retrying');
        debug.warn('[voiceNoteUploads] upload failed; will retry', { note_id: noteId, attempts: written.attempts });
      }
      changed();
    }
    if (memberKey() === member) schedule(await load(member));
  })().finally(() => { draining = null; });
  return draining;
}

/** On launch (and sign-in, and the app coming back): send whatever didn't finish. Failed ones wait for Retry. */
export async function resumeVoiceNoteUploads(): Promise<void> {
  const member = memberKey();
  if (!member) return;
  const list = await locked(async () => {
    const current = await load(member);
    for (const item of current) if (!item.failed) item.nextAt = 0;
    await save(member, current);
    return current;
  });
  for (const item of list) setState(member, item, item.failed ? 'failed' : 'retrying');
  changed();
  await drainVoiceNoteUploads();
}

const findRecord = async (member: string, noteId: number | null, ref?: string | null) =>
  (await load(member)).find((p) => (ref && p.ref === ref) || (noteId != null && p.noteId === noteId)) || null;

/** "Couldn't upload" → Retry: start again from a fresh count. */
export async function retryVoiceNoteUpload(noteId: number | null, ref?: string | null): Promise<void> {
  const member = memberKey();
  if (!member) return;
  const item = await findRecord(member, noteId, ref);
  if (!item) return;
  await update(member, item.ref, (p) => ({ ...p, failed: false, attempts: 0, nextAt: 0 }));
  setState(member, item, 'uploading');
  changed();
  await drainVoiceNoteUploads();
}

/** The note is gone (deleted or discarded): stop sending and delete the local file. */
export async function cancelVoiceNoteUpload(noteId: number | null, ref?: string | null): Promise<void> {
  const member = memberKey();
  if (!member) return;
  const item = await findRecord(member, noteId, ref);
  if (!item) return;
  await update(member, item.ref, () => null);
  await deleteLocal(item.path);
  setState(member, item, null);
  changed();
}

function hookAppState() {
  if (appStateHooked) return;
  appStateHooked = true;
  AppState.addEventListener('change', (next) => {
    if (next === 'active') void resumeVoiceNoteUploads();
  });
}

/** For tests only. */
export function resetVoiceNoteUploads() {
  memory.clear();
  states.clear();
  listeners.clear();
  draining = null;
  lock = Promise.resolve();
  fresh.clear();
  ensuring.clear();
  if (timer) clearTimeout(timer);
  timer = null;
  appStateHooked = false;
}
